import Decimal from 'decimal.js';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { getTankContentInBerthCall } from 'src/utils/stowage/manifest/cargoManifestUtils';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';
import { notStowedWeightInTanks } from 'src/utils/stowage/stowageOperation/stowageOperationUtils';
import { getCargoTemperature } from 'src/utils/stowage/stowageOperation/cargoTemperatureUtils';
import { getAdjustedCargoTemperatureInTank, updateAdjustedCargoTemperature } from 'src/utils/stowage/stowageOperation/adjustedCargoTemperatureUtils';
import { AdjustedCargoTemperature } from 'src/types/ViewModel/Stowage/AdjustedCargoTemperature';
import { getMaxStowedWeight, getMaxUnstowedWeight } from 'src/utils/stowage/stowageOperation/stowageFillUtils';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { createStowageItemChangeData } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/StowageItemChangeDataFactory';
import { areCargoCommingleable } from 'src/utils/stowage/stowageOperation/cargoUtils';
import { isPositive } from 'src/utils/stowage/decimalsUtils';

const logFillTanks: boolean = false;
const logSpecificTankOperations: boolean = false;
const MONITORED_TANK_NAME: string = '8WS';

/*
This function will return true only if:
- the "not stowed weight" for this cargo operation is greater than 0 OR there are existing cargo contents
- the cargo operation is a loading operation and there are no non-commingleable cargo contents with weight > 0 in the tanks
- the cargo operation is an unloading operation and one of the cargo contents is the cargo operation's cargo
*/
export const canFillTanks = (stowagePlan: StowagePlan, tanks: Tank[], cargoOperation: RotationCargoOperation): boolean => {
    if (!stowagePlan) return false;

    const berthCall = cargoOperation.berthCall;
    const tankContents = tanks.map((t) => getTankContentInBerthCall(stowagePlan, berthCall, t)); // TODO: NULL-SAFETY - Using flatMap with potential undefined values - getTankContentInBerthCall could return null/undefined, causing flatMap to fail. Should filter out nulls first: tankContents.filter(t => t).flatMap(...)
    const cargoContents = tankContents.flatMap((t) => t?.cargoContents).filter((t) => t && isPositive(t.weight));
    const cargoContentsWithWeight = cargoContents.filter((c) => isPositive(c.weight));
    const notStowedWeight = notStowedWeightInTanks(stowagePlan, cargoOperation);

    const result =
        stowagePlan &&
        tanks?.length > 0 &&
        (notStowedWeight.gt(0) || cargoContents.length > 0) &&
        ((cargoOperation.operationType === 'Loading' &&
            (cargoContentsWithWeight.length === 0 || cargoContentsWithWeight.every((o) => areCargoCommingleable(o.cargo, cargoOperation.cargo)))) ||
            (cargoOperation.operationType === 'Unloading' && cargoContents.length > 0));

    return result;
};

export const fillTanks = (
    stowagePlan: StowagePlan,
    cargoContentsPerTankAtDeparture: { tank: Tank; cargoContent: CargoContent }[],
    cargoOperation: RotationCargoOperation,
    fillWeight: Decimal
) => {
    if (logFillTanks) console.log('fillTanks: Starting with fillWeight:', fillWeight.toNumber());

    const cargoTemperature = getCargoTemperature(stowagePlan, cargoOperation.cargo);
    let remainingWeightToStow = fillWeight;
    const filledTanks: Tank[] = [];
    let stowagePlanChanges: StowagePlanChange[] = [];

    cargoContentsPerTankAtDeparture.forEach(({ tank, cargoContent }) => {
        if (logSpecificTankOperations && tank.tankName === MONITORED_TANK_NAME) {
            console.log(`--- ${MONITORED_TANK_NAME} Tank Operation Details ---`);
            console.log(`${MONITORED_TANK_NAME}: Operation type:`, cargoOperation.operationType);
            console.log(`${MONITORED_TANK_NAME}: Current cargo content weight:`, cargoContent?.weight?.toNumber() ?? 0);
            console.log(`${MONITORED_TANK_NAME}: Remaining weight to stow:`, remainingWeightToStow.toNumber());
        }

        const cargoTemperatureInTank = getAdjustedCargoTemperatureInTank(stowagePlan, cargoOperation.cargo, tank)?.temperature ?? null;
        const actualCargoTemperature = cargoTemperatureInTank ?? cargoTemperature;
        const cargoContentWeight = cargoContent?.weight ?? new Decimal(0);
        const fillWeightInTank =
            cargoOperation.operationType === 'Loading'
                ? getMaxStowedWeight(
                      cargoOperation.cargo,
                      new Decimal(tank.maxWeightAt98Percent),
                      new Decimal(tank.volumeAt98Percent),
                      cargoContentWeight,
                      remainingWeightToStow,
                      actualCargoTemperature
                  )
                : cargoContent
                ? getMaxUnstowedWeight(cargoContentWeight, remainingWeightToStow.abs()).negated()
                : new Decimal(0);

        if (logSpecificTankOperations && tank.tankName === MONITORED_TANK_NAME) {
            console.log(`${MONITORED_TANK_NAME}: Fill weight calculated:`, fillWeightInTank.toNumber());
            console.log(`${MONITORED_TANK_NAME}: Cargo temperature:`, actualCargoTemperature);
            console.log(`${MONITORED_TANK_NAME}: Max tank capacity:`, tank.maxWeightAt98Percent);
        }

        const fillTankResponse = fillTank(
            stowagePlan,
            cargoOperation,
            tank,
            fillWeightInTank,
            actualCargoTemperature,
            cargoTemperatureInTank !== null,
            cargoOperation.operationType === 'Loading'
        );

        if (fillTankResponse?.tank) {
            filledTanks.push(fillTankResponse.tank);
            stowagePlanChanges = stowagePlanChanges.concat(fillTankResponse.stowagePlanChanges);
            remainingWeightToStow = remainingWeightToStow.minus(fillWeightInTank);

            if (logSpecificTankOperations && tank.tankName === MONITORED_TANK_NAME) {
                console.log(`${MONITORED_TANK_NAME}: Tank successfully filled/modified`);
                console.log(`${MONITORED_TANK_NAME}: New remaining weight to stow:`, remainingWeightToStow.toNumber());
                console.log(`--- End of ${MONITORED_TANK_NAME} Tank Operation ---`);
            }
        } else if (logSpecificTankOperations && tank.tankName === MONITORED_TANK_NAME) {
            console.log(`${MONITORED_TANK_NAME}: Failed to fill/modify tank`);
            console.log(`--- End of ${MONITORED_TANK_NAME} Tank Operation ---`);
        }
    });

    if (logFillTanks) console.log('fillTanks: Completed. Filled', filledTanks.length, 'tank(s). Remaining weight:', remainingWeightToStow.toNumber());

    return { filledTanks, stowagePlanChanges };
};

export const fillTank = (
    stowagePlan: StowagePlan,
    cargoOperation: RotationCargoOperation,
    tank: Tank,
    stowedWeightInTank: Decimal,
    cargoTemperature: number,
    isCargoTemperatureAdjusted: boolean,
    overwriteWeight: boolean
) => {
    const isMonitoredTank = tank.tankName === MONITORED_TANK_NAME;

    if (stowedWeightInTank.lte(0) && cargoOperation.operationType !== 'Unloading') {
        if (logSpecificTankOperations && isMonitoredTank) {
            console.log(`${MONITORED_TANK_NAME} fillTank: Stowed weight is <= 0 for non-unloading operation, returning null`);
        }
        return null;
    }

    const stowageItem = stowagePlan.stowageItems.find((s) => s.tankId === tank.tankId && s.cargoOperationGuid === cargoOperation.cargoOperationGuid);
    const originalWeight = stowageItem ? stowageItem.weight : new Decimal(0);

    if (logSpecificTankOperations && isMonitoredTank) {
        console.log(`${MONITORED_TANK_NAME} fillTank: Original stowage weight:`, originalWeight.toNumber());
        console.log(`${MONITORED_TANK_NAME} fillTank: Operation mode:`, overwriteWeight ? 'Overwrite' : 'Update');
    }
    let finalWeight = stowedWeightInTank;

    if (logSpecificTankOperations && isMonitoredTank) {
        console.log(`${MONITORED_TANK_NAME} fillTank: Input stowed weight:`, stowedWeightInTank.toNumber());
        if (cargoOperation.operationType === 'Unloading') {
            console.log(`${MONITORED_TANK_NAME} fillTank: Unloading amount (absolute):`, stowedWeightInTank.abs().toNumber());
        }
    }
    const stowagePlanChanges: StowagePlanChange[] = [];
    let stowageChangeData;
    let newStowageItem;
    if (!stowageItem) {
        // For new stowage items during unloading operations:
        // stowedWeightInTank is already negative (from getMaxUnstowedWeight), so we use it directly
        // This ensures the stowage item has the correct negative weight representing removal
        if (cargoOperation.operationType === 'Unloading') {
            // stowedWeightInTank is already negative from getMaxUnstowedWeight, use it as-is
            finalWeight = stowedWeightInTank;
        }

        newStowageItem = {
            // TODO: CRYPTO-IMPORT - crypto.randomUUID() usage - should import crypto or use a proper UUID generator. This may fail in some environments.
            stowageGuid: crypto.randomUUID(),
            tankId: tank.tankId,
            cargoOperationGuid: cargoOperation.cargoOperationGuid,
            cargoOperation: cargoOperation,
            tank: tank,
            weight: finalWeight,
        };
        stowageChangeData = createStowageItemChangeData(newStowageItem);
        stowagePlan.stowageItems.push(newStowageItem);

        if (logSpecificTankOperations && isMonitoredTank) {
            console.log(`${MONITORED_TANK_NAME} fillTank: Created new stowage item with weight:`, finalWeight.toNumber());
        }
    } else {
        if (!overwriteWeight) {
            if (cargoOperation.operationType === 'Unloading') {
                if (originalWeight.gt(0)) {
                    // Original weight is positive: subtract the unloading amount
                    const unloadingAmount = stowedWeightInTank.abs();
                    finalWeight = originalWeight.minus(unloadingAmount);

                    // Ensure we don't go below zero for remaining weight when starting with positive inventory
                    if (finalWeight.lt(0)) {
                        finalWeight = new Decimal(0);
                    }

                    if (logSpecificTankOperations && isMonitoredTank) {
                        console.log(
                            `${MONITORED_TANK_NAME} fillTank: Calculated final weight for unloading from positive: ${originalWeight.toNumber()} - ${unloadingAmount.toNumber()} = ${finalWeight.toNumber()}`
                        );
                    }
                } else {
                    // Original weight is zero or negative: accumulate unloading (make more negative)
                    const additionalUnloading = stowedWeightInTank.abs();
                    finalWeight = originalWeight.minus(additionalUnloading);

                    if (logSpecificTankOperations && isMonitoredTank) {
                        console.log(
                            `${MONITORED_TANK_NAME} fillTank: Accumulated unloading weight from zero/negative: ${originalWeight.toNumber()} - ${additionalUnloading.toNumber()} = ${finalWeight.toNumber()}`
                        );
                    }
                }
            } else if (cargoOperation.operationType === 'Loading' && originalWeight.gt(0)) {
                finalWeight = originalWeight.plus(stowedWeightInTank);

                if (logSpecificTankOperations && isMonitoredTank) {
                    console.log(`${MONITORED_TANK_NAME} fillTank: Accumulated loading weight:`, finalWeight.toNumber());
                }
            }
        } else {
            // When overwriteWeight=true
            if (cargoOperation.operationType === 'Unloading') {
                // For unloading with overwrite, stowedWeightInTank is already negative from getMaxUnstowedWeight
                // Use it directly to maintain consistency with new stowage item creation
                finalWeight = stowedWeightInTank;

                if (logSpecificTankOperations && isMonitoredTank) {
                    console.log(`${MONITORED_TANK_NAME} fillTank: Set weight for unloading (overwrite):`, finalWeight.toNumber());
                }
            }
            // For loading with overwrite, finalWeight is already set to stowedWeightInTank
        }

        stowageItem.weight = finalWeight;
        stowageChangeData = createStowageItemChangeData(stowageItem);
        newStowageItem = stowageItem;

        if (logSpecificTankOperations && isMonitoredTank) {
            console.log(`${MONITORED_TANK_NAME} fillTank: Updated existing stowage item to weight:`, finalWeight.toNumber());
        }
    }

    stowagePlanChanges.push(
        createStowagePlanChange(stowagePlan.stowagePlanGuid, newStowageItem.stowageGuid, StowageChangeDataType.StowageItem, stowageChangeData)
    );

    if (isCargoTemperatureAdjusted) {
        const adjustedCargoTemperature: AdjustedCargoTemperature = {
            // TODO: CRYPTO-IMPORT - crypto.randomUUID() usage - should import crypto or use a proper UUID generator. This may fail in some environments.
            adjustedCargoTemperatureGuid: crypto.randomUUID(),
            cargo: cargoOperation.cargo,
            cargoGuid: cargoOperation.cargoGuid,
            tank: tank,
            tankId: tank.tankId,
            temperature: cargoTemperature,
        };
        const response = updateAdjustedCargoTemperature(stowagePlan, adjustedCargoTemperature);
        stowagePlanChanges.push(
            createStowagePlanChange(
                stowagePlan.stowagePlanGuid,
                response.adjustedCargoTemperatureGuid,
                StowageChangeDataType.AdjustedCargoTemperature,
                response.changedData
            )
        );

        if (logSpecificTankOperations && isMonitoredTank) {
            console.log(`${MONITORED_TANK_NAME} fillTank: Updated adjusted cargo temperature to:`, cargoTemperature);
        }
    }

    return { tank, stowagePlanChanges };
};
